import { ApolloError, useQuery } from "@apollo/client";
import React, { useEffect, useRef, useState } from "react";

import { Sentry } from "@compono/sentry-browser";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogHeading,
  DialogPrimaryActions,
  Heading,
  HeadingProps,
  ResponsiveCss,
  Stack,
  Text,
  createContext,
} from "@compono/ui";

import { ExclamationTriangleOutlineIcon } from "@compono/ui-icons";

// import * as analytics from "../../../shortlyster-ui/src/lib/auth/analytics";
import { SectionLoader } from "~/components/SectionLoader";
import { useAuthentication } from "~/hooks/useAuthentication";
import { useApolloClients } from "~/hooks/useApolloClients";

import { resolveDevelopPermissions, resolvePermissions } from "./permissions";
import type { Permissions } from "./permissions";
import {
  FetchEmployerProfile,
  FetchEngagePermissions,
  FetchEmployerPermissions,
  EmployerPermissions,
  EngagePermissions as NativeEngagePermissions,
  FetchTrackyrPermissions,
  TrackyrPermissions,
} from "./queries";
import {
  Employer,
  FetchEmployerProfileData,
  Organisation,
  User,
} from "./types";
import {
  useFetchDevelopPermissionsQuery,
  useMyGroupMembersQuery,
} from "~/lib/federationGateway/platformGraphql";
import { getCachedAuthContext } from "../AuthenticationProvider";

export type AuthorizationContext = {
  user: User;
  userId: string;
  orgId: string;
  organisation: Organisation;
  permissions: Permissions;
};

const [useAuthorization, AuthorizationContext] =
  createContext<AuthorizationContext>({
    name: "Authorization",
  });

const AuthorizationConsumer = AuthorizationContext.Consumer;

export type AuthorizationProviderProps = {
  children: React.ReactNode;
};

const AuthorizationProvider = ({ children }: AuthorizationProviderProps) => {
  const {
    loginWithRedirect,
    logout,
    isAuthenticated,
    isAutoSigningIn,
    orgId,
    removeUser,
    events,
  } = useAuthentication();
  const {
    employerClient,
    engageClient,
    shortlysterClient,
    trackyrClient,
    platformFederationGatewayClient,
  } = useApolloClients();
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>();
  const [hasNoOrgAccess, setHasNoOrgAccess] = useState<boolean | undefined>(
    undefined,
  );

  // TODO @jg useEffect(() => analytics.terminate, []);

  // try to re-authenticate the user
  const triggerReauthenticate = () => {
    return loginWithRedirect({
      state: {
        returnTo: `${window.location?.pathname}${window.location?.search}`,
      },
    });
  };

  // logout user
  const triggerLogout = () => {
    window.sessionStorage.clear();
    return logout({
      state: {
        returnTo: `${window.location?.pathname}${window.location?.search}`,
      },
    });
  };

  const onError = (e: ApolloError) => {
    Sentry.flush(3000).then(() => {
      console.error("AuthorizationProvider error, triggering re-auth", e);
      triggerReauthenticate();
    });
  };

  // skip queries if we are auto signing in or if there is no orgId
  const shouldSkip = isAutoSigningIn || !orgId;

  useEffect(() => {
    return events.addAccessTokenExpired(() => {
      setIsSessionExpired(true);
    });
  }, [events]);

  const handleSessionExpiredAcknowledgement = () => {
    removeUser();
    triggerReauthenticate();
  };

  const { data, loading } = useQuery<FetchEmployerProfileData>(
    FetchEmployerProfile,
    {
      variables: { organisationId: orgId },
      client: shortlysterClient,
      fetchPolicy: "network-only",
      onError,
      skip: shouldSkip,
    },
  );

  const { data: tpData, loading: tpLoading } = useQuery<{
    permissions: TrackyrPermissions;
  }>(FetchTrackyrPermissions, {
    variables: { organisationId: orgId },
    client: trackyrClient,
    onError,
    skip: shouldSkip,
  });

  const { data: epData, loading: epLoading } = useQuery<{
    permissions: EmployerPermissions;
    recruiterProfile: Employer;
  }>(FetchEmployerPermissions, {
    variables: { organisationId: orgId },
    client: employerClient,
    onError,
    skip: shouldSkip,
  });

  // Both Develop reads are on the app's critical path and mount together, so
  // they opt into batching to share one gateway container acquisition rather
  // than pay for two.
  const developReadOptions = {
    client: platformFederationGatewayClient,
    context: { batch: true },
    skip: shouldSkip,
  };

  const { data: developData, loading: developLoading } =
    useFetchDevelopPermissionsQuery(developReadOptions);

  const { data: groupsData, loading: groupsLoading } =
    useMyGroupMembersQuery(developReadOptions);

  const {
    data: enpData,
    loading: enpLoading,
    error: enpError, // Cannot read properties of undefined (reading 'code') for user's without access to Engage
  } = useQuery<{
    permissions: NativeEngagePermissions;
  }>(FetchEngagePermissions, {
    variables: { orgId },
    client: engageClient,
    context: { headers: { orgId } },
    skip: shouldSkip,
    // onError, error is "normal" when user does not have engage perms
  });

  const employerHasNoOrgAccess =
    !loading && data?.recruiterProfile?.organisations?.length === 0;

  useEffect(() => {
    if (isAuthenticated && (orgId === false || employerHasNoOrgAccess)) {
      console.warn(
        "User does not belong to any organisations, showing error page",
      );
      setHasNoOrgAccess(true);
    }
  }, [orgId, isAuthenticated, employerHasNoOrgAccess]);

  // Show error page if user does not belong to any organisation,
  // This prevents the infinite redirect loop (re-auth -> query fail -> re-auth)
  if (hasNoOrgAccess === true) {
    return <NoOrganisationPage onSignOut={triggerLogout} />;
  }

  if (
    isAutoSigningIn ||
    loading ||
    tpLoading ||
    enpLoading ||
    epLoading ||
    developLoading ||
    groupsLoading ||
    !data ||
    !tpData ||
    (!enpData && !enpError) ||
    !epData
    // ||!developData
  ) {
    return (
      <Box sx={{ height: "100vh" }}>
        <SectionLoader />
      </Box>
    );
  }

  const { currentOrganisation } = data.recruiterProfile;

  // get all product codes for the current organisation
  const allProductCodes =
    epData.recruiterProfile.currentOrganisation.products?.map(
      (p) => p.productCode,
    ) || [];
  // use roles from employer gateway as it returns all roles instead of just the ones for the current product
  const allRoles = epData.recruiterProfile.accesses
    ?.find((a) => a.organisationId === orgId)
    ?.allAppsRoles?.filter((r) => allProductCodes.includes(r.appCode));

  const developPermissions = resolveDevelopPermissions(developData, groupsData);
  const permissions = {
    ...resolvePermissions(
      allRoles,
      data.permissions,
      tpData.permissions,
      epData?.permissions,
      enpData?.permissions,
    ),
    develop: developPermissions,
  };

  return (
    <AuthorizationContext.Provider
      value={{
        user: {
          ...data.profile,
          ...data.recruiterProfile,
          // overwrites with data from employer gateway
          // this is due to the fact that other gateways filter accesses by product
          accesses: epData.recruiterProfile.accesses,
        },
        userId: data.profile.id,
        orgId: currentOrganisation?.id || "",
        organisation: currentOrganisation,
        permissions,
      }}
    >
      {children}
      <SessionExpiredDialog
        isOpen={isSessionExpired}
        onClose={handleSessionExpiredAcknowledgement}
      />
    </AuthorizationContext.Provider>
  );
};

const ErrorIcon = ({
  color = "feedback-warning-onsurface",
  backgroundColor = "feedback-warning-surface",
}: {
  color?: string;
  backgroundColor?: string;
}) => (
  <Box
    as="section"
    sx={{
      borderRadius: "50%",
      backgroundColor,
      color,
      width: ["3rem", "4rem"],
      height: ["3rem", "4rem"],
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <Box
      sx={{
        width: ["1.5rem", "2rem"],
        height: ["1.5rem", "2rem"],
        marginBottom: "0.125rem",
      }}
    >
      <ExclamationTriangleOutlineIcon
        style={{
          width: "100%",
          height: "100%",
        }}
      />
    </Box>
  </Box>
);

const ErrorTitle = ({ level }: { level: HeadingProps["level"] }) => (
  <Heading as="h2" level={level} align="center">
    Access restricted
  </Heading>
);

const NoOrganisationPage = ({ onSignOut }: { onSignOut: () => void }) => (
  <Box
    sx={{
      display: "flex",
      width: "100vw",
      height: "100vh",
      alignItems: ["start", "center"],
      justifyContent: "center",
      backgroundColor: "primary.3",
    }}
  >
    <Box sx={{ padding: "0 1.5rem" }}>
      <Card
        elevation="4"
        border={true}
        as="main"
        sx={{
          maxWidth: ["100%", "40rem"],
          padding: ["2rem", "3rem"],
          marginTop: ["4rem", "0"],
        }}
      >
        <Stack gap={4} align="center">
          <ErrorIcon />
          <ResponsiveCss max="sm" useDisplay>
            <ErrorTitle level="5" />
          </ResponsiveCss>
          <ResponsiveCss min="md" useDisplay>
            <ErrorTitle level="4" />
          </ResponsiveCss>
          <Text align="center" tone="subtle">
            You do not currently have permission to access this area. Please
            ensure you have accepted any pending invitations to Compono or
            contact your administrator for assistance.
          </Text>
          <Button onClick={onSignOut} look="secondary" tone="warning">
            Sign out
          </Button>
        </Stack>
      </Card>
    </Box>
  </Box>
);

const SessionExpiredDialog = (props: {
  isOpen?: boolean;
  onClose?: () => void;
}) => {
  const dialogRef = useRef(null);
  const { isOpen = false, onClose } = props;
  return (
    <Dialog
      ref={dialogRef}
      isOpen={isOpen}
      size="sm"
      onDismiss={onClose}
      aria-describedby="dialog-description-session-expired"
      aria-labelledby="dialog-title-session-expired"
    >
      <DialogHeading id="dialog-title-session-expired">
        You have been signed out
      </DialogHeading>
      <DialogBody id="dialog-description-session-expired">
        <Text>
          Looks like you've been away for a bit. We've signed you out to protect
          your account.
        </Text>
      </DialogBody>
      <DialogPrimaryActions>
        <Button onClick={onClose}>Sign in again</Button>
      </DialogPrimaryActions>
      <DialogCloseButton onClick={onClose} />
    </Dialog>
  );
};

export {
  useAuthorization,
  AuthorizationProvider,
  AuthorizationConsumer as AuthConsumer,
  AuthorizationContext,
};
